import { IWorkspace, IWorkspaceFolder, WorkbenchState, ISingleFolderWorkspaceIdentifier, IWorkspaceIdentifier, isSingleFolderWorkspaceIdentifier,
    IClientWorkspaceContextService as IWorkspaceContextService,
    IConfigurationOverrides,
    ConfigurationTarget,
    IWorkspaceFolderData,
    WorkspaceFolder,
    IWorkspaceFoldersChangeEvent,
    GetWorkspaceDocument,
    CacheTypenames,
    GetWorkspaceFolderContextDocument,
    IGetWorkspaceFolderContextQuery,
    IGetWorkspaceFolderContextQueryVariables,
    LifecyclePhase,
} from '@workbench-stack/core';
import * as core from '@workbench-stack/core';
import { URI } from '@vscode-alt/monaco-editor/esm/vs/base/common/uri';
import { injectable, inject, postConstruct } from 'inversify';
import { ApolloClient } from 'apollo-client';
import { ClientTypes as CommonTypes } from '@common-stack/client-core';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { FetchPolicy } from 'apollo-client';
import { merge } from 'lodash';

@injectable()
export class WorkspaceContextService extends core.AbstractWorkspaceContextService implements IWorkspaceContextService {

    constructor(
        @inject(core.ClientTypes.ILifecycleService)
        private lifecycleService: core.ILifecycleService,
        @inject(CommonTypes.ApolloClient)
        private client: ApolloClient<any>,
        @inject(CommonTypes.InMemoryCache)
        private cache: InMemoryCache,
        @inject(CommonTypes.UtilityClass)
        private utility,
        @inject(CommonTypes.Logger)
        protected logger,
    ) {
        super(logger);
    }

    @postConstruct()
    public async extInitialize() {
        if (!this.workspace) {
            await this.asyncInitializedWorkspaceContainer();
                // Signal to lifecycle that services are set
                this.lifecycleService.phase = core.LifecyclePhase.Restored;
        }
    }

    public initialize(args: any): any {
        return this.createWorkspace(args).then(workspaceContext => {
            this.workspace = this.reviveWorkspaceUris(workspaceContext);
            const data = {
                id: this.workspace.id,
                name: this.workspace.name,
                folders: this.workspace.folders.map((folder) => ({ ...folder, __typename: CacheTypenames.WorkspaceFolderData })),
                configuration: this.workspace.configuration,
                __typename: CacheTypenames.WorkspaceData,
            };
            this.client.writeQuery({
                query: GetWorkspaceDocument,
                data: { getWorkspace: data },
            });
            this.lifecycleService.phase = LifecyclePhase.Restored;
            return true;
        });
    }

    private async createWorkspace(arg) {
        return await this.loadByWorkspaceName(arg);
    }

    private async getWorkspaceContext(arg: any, opts: { fetchPolicy: FetchPolicy } = { fetchPolicy: 'cache-first' }) {
        return await this.client.query<IGetWorkspaceFolderContextQuery, IGetWorkspaceFolderContextQueryVariables>({
            query: GetWorkspaceFolderContextDocument,
            variables: { arg },
            fetchPolicy: opts.fetchPolicy,
        }).then(({ data: { getWorkspaceFolderContext } }) => this.reviveWorkspaceUris(getWorkspaceFolderContext));
    }

    private async loadByWorkspaceName(args) {
        return await this.getWorkspaceContext(args);
    }

    private async asyncInitializedWorkspaceContainer(opts: { fetchPolicy: FetchPolicy} = { fetchPolicy: 'cache-first'}) {
        await this.lifecycleService.when(core.LifecyclePhase.Ready).then(() =>  {
            return this.client.query<core.IGetWorkspace_WSQuery>({
                query: core.GetWorkspace_WSDocument,
                fetchPolicy: opts.fetchPolicy,
            }).then(({ data: { getWorkspace }}) => this.workspace = this.reviveWorkspaceUris(getWorkspace));
        });
    }


    private toWorkspaceFolders(folders: IWorkspaceFolderData[]) {
        return folders.map((folder) => {
            const uri = URI.revive(folder.uri);
            return new WorkspaceFolder({ uri: uri, index: 0, name: folder.name }, { uri: uri.toString() });
        });
    }

    private compareFolders(currentFolders: IWorkspaceFolderData[], newFolders: IWorkspaceFolderData[]): IWorkspaceFoldersChangeEvent {
        const result = { added: [], removed: [], changed: [] } as IWorkspaceFoldersChangeEvent;
        result.added = newFolders.filter(newFolder => !currentFolders.some(currentFolder => newFolder.uri.toString() === currentFolder.uri.toString()));
        for (let currentIndex = 0; currentIndex < currentFolders.length; currentIndex++) {
            let currentFolder = currentFolders[currentIndex];
            let newIndex = 0;
            for (newIndex = 0; (newIndex < newFolders.length) && (currentFolder.uri.toString() !== newFolders[newIndex].uri.toString()); newIndex++) {}
            if (newIndex < newFolders.length) {
                if (currentIndex !== newIndex || currentFolder.name !== newFolders[newIndex].name) {
                    result.changed.push(currentFolder);
                }
            } else {
                result.removed.push(currentFolder);
            }
        }
        return result;
    }

    private onWorkspaceFoldersChanged(foldersData: IWorkspaceFolderData[]) {
        if (this.workspace && this.workspace.configuration) {

            let configuredFolders = this.toWorkspaceFolders(foldersData);
            const changes = this.compareFolders(this.workspace.folders, configuredFolders);
            if (changes.added.length || changes.removed.length || changes.changed.length) {
                this.workspace.folders = configuredFolders;
                // this._onDidChangeWorkspaceFolders.fire(changes);
                const data = {
                    id: this.workspace.id,
                    folders: foldersData.map(folder => ({ ...folder, __typename: CacheTypenames.WorkspaceFolderData })),
                    configuration: this.workspace.configuration,
                    __typename: CacheTypenames.WorkspaceData,
                };
                this.client.writeQuery({
                    query: GetWorkspaceDocument,
                    data: { getWorkspace: data },
                });
            }
        }
    }

    public async addFolders(foldersToAdd: core.IWorkspaceFolderCreationData[], index?: number): Promise<void> {
        const { data } = await this.client.mutate<core.IUpdateWorkspaceContextAddFoldersMutation>({
            mutation: core.UpdateWorkspaceContextAddFoldersDocument,
            variables: { foldersToAdd, index },
        });
        this.onWorkspaceFoldersChanged(data.updateWorkspaceContextAddFolders);
    }
    public async removeFolders(foldersToRemove: URI[]): Promise<void> {
        const { data } = await this.client.mutate<core.IUpdateWorkspaceContextRemoveFoldersMutation>({
            mutation: core.UpdateWorkspaceContextRemoveFoldersDocument,
            variables: { foldersToRemove },
        });
        this.onWorkspaceFoldersChanged(data.updateWorkspaceContextRemoveFolders);
    }
    public async updateFolders(foldersToAdd: core.IWorkspaceFolderCreationData[], foldersToRemove: URI[], index?: number): Promise<void> {
        const { data } = await this.client.mutate<core.IUpdateWorkspaceContextUpdateFoldersMutation>({
            mutation: core.UpdateWorkspaceContextUpdateFoldersDocument,
            variables: { foldersToAdd, foldersToRemove, index },
        });
        this.onWorkspaceFoldersChanged(data.updateWorkspaceContextUpdateFolders);
    }

}
